import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { Movie } from '@/mocks/movies';
import Rating from '@/components/base/Rating';

interface HeroCarouselProps {
  movies: Movie[];
}

export default function HeroCarousel({ movies }: HeroCarouselProps) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (movies.length <= 1) return undefined;
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % movies.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [movies.length]);

  if (movies.length === 0) return null;

  const goTo = (next: number) => setIndex((next + movies.length) % movies.length);
  const active = movies[index];

  return (
    <section className="relative h-[440px] w-full overflow-hidden rounded-lg bg-primary-950 md:h-[560px]">
      {movies.map((movie, i) => (
        <img
          key={movie.id}
          src={movie.backdrop}
          alt={`${movie.title} 剧照`}
          title={`${movie.title} 焦点推荐`}
          className={`absolute inset-0 h-full w-full object-cover object-top transition-opacity duration-700 ${
            i === index ? 'opacity-100' : 'opacity-0'
          }`}
        />
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950/95 via-primary-950/55 to-primary-950/20" />

      <div className="absolute inset-x-0 bottom-0 w-full p-6 md:p-10">
        <div className="max-w-2xl">
          <span className="inline-flex items-center gap-1 rounded-full bg-accent-500 px-3 py-1 font-label text-xs font-semibold text-primary-950">
            <i className="ri-fire-fill" aria-hidden="true" />
            焦点推荐
          </span>
          <h2 className="mt-4 font-heading text-2xl font-black text-background-50 md:text-5xl">
            {active.title}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 font-label text-sm text-background-200">
            <Rating value={active.rating} size="md" className="text-accent-400" />
            <span>{active.year}</span>
            <span>{active.region}</span>
            <span>{active.genres.join(' / ')}</span>
          </div>
          <p className="mt-4 hidden max-w-xl text-sm leading-relaxed text-background-200 md:block">
            {active.summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="flex items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 font-label text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400 cursor-pointer"
            >
              <i className="ri-play-circle-line text-lg" aria-hidden="true" />
              查看详情
            </Link>
            <Link
              to={active.type === 'movie' ? '/category/movie' : '/category/tv'}
              className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-200/60 bg-background-50/10 px-5 py-2.5 font-label text-sm text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/20 cursor-pointer"
            >
              浏览同类
            </Link>
          </div>
        </div>
      </div>

      <button
        type="button"
        onClick={() => goTo(index - 1)}
        aria-label="上一张"
        className="absolute left-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/40 md:left-6 md:h-10 md:w-10 cursor-pointer"
      >
        <i className="ri-arrow-left-s-line text-2xl" aria-hidden="true" />
      </button>
      <button
        type="button"
        onClick={() => goTo(index + 1)}
        aria-label="下一张"
        className="absolute right-3 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-background-50/20 text-background-50 backdrop-blur-sm transition-colors hover:bg-background-50/40 md:right-6 md:h-10 md:w-10 cursor-pointer"
      >
        <i className="ri-arrow-right-s-line text-2xl" aria-hidden="true" />
      </button>

      <div className="absolute bottom-6 right-6 hidden items-center gap-2 md:flex">
        {movies.map((movie, i) => (
          <button
            key={movie.id}
            type="button"
            onClick={() => goTo(i)}
            aria-label={`切换到 ${movie.title}`}
            className={`h-2 rounded-full transition-all cursor-pointer ${
              i === index ? 'w-8 bg-accent-500' : 'w-2 bg-background-50/50 hover:bg-background-50/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}